import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { DialogActions, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { AlineButton, AlineDialog } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useBulkExport } from 'src/hooks/useBulkExport';
import { makeStyles } from 'tss-react/mui';

import { addBulkExportTask } from './bulk-export-tasks';

interface BulkExportDialogProps {
  documentCount: number;
  onClose: () => void;
  open: boolean;
  documentIds?: UUID[];
  /**
   * MUI X filter model from the live grid. Passed through for report-based
   * select-all so the server export matches what the user actually sees.
   * Ignored when `documentIds` is provided.
   */
  filterModel?: Apollo.RequestBulkExportInput['filterModel'];
  reportId?: UUID;
}

const FORMAT_OPTIONS: {
  description: string;
  label: string;
  value: Apollo.BulkExportFormat;
}[] = [
  {
    description: 'Every document is rendered to PDF.',
    label: 'PDF',
    value: Apollo.BulkExportFormat.Pdf,
  },
  {
    description: 'Signed → PDF · Unsigned → DOCX · Uploads keep their original file.',
    label: 'Original',
    value: Apollo.BulkExportFormat.Original,
  },
];

const useStyles = makeStyles()(theme => ({
  description: {
    marginBottom: theme.spacing(2),
  },
  expiry: {
    marginTop: theme.spacing(1),
  },
  formatLabel: {
    alignItems: 'flex-start',
    marginBottom: theme.spacing(0.5),
    marginLeft: 0,
    marginRight: 0,
  },
  formatOptionDescription: {
    color: theme.palette.text.secondary,
    display: 'block',
  },
  formatOptionPrimary: {
    paddingTop: theme.spacing(0.5),
  },
  formatSection: {
    marginBottom: theme.spacing(1),
  },
  formatTitle: {
    marginBottom: theme.spacing(0.5),
  },
  paper: {
    width: 566,
  },
  primaryButton: {
    marginLeft: theme.spacing(1),
  },
}));

export function BulkExportDialog({
  documentCount,
  documentIds,
  filterModel,
  onClose,
  open,
  reportId,
}: BulkExportDialogProps): JSX.Element {
  const { classes } = useStyles();
  const [format, setFormat] = useState<Apollo.BulkExportFormat>(Apollo.BulkExportFormat.Pdf);
  const { loading, requestBulkExport } = useBulkExport();

  async function handleConfirm(): Promise<void> {
    const result = await requestBulkExport({ documentIds, filterModel, format, reportId });
    if (result?.id) {
      // Hand the export off to the shared background-task tray, which owns its
      // status panel for the rest of the job's life (across dialog close + nav).
      addBulkExportTask(result.id);
      onClose();
    }
  }

  return (
    <AlineDialog
      classes={{ paper: classes.paper }}
      loading={loading}
      open={open}
      title={`Export ${documentCount.toLocaleString()} documents`}
      onClose={onClose}
    >
      <Typography align="justify" className={classes.description} variant="body2">
        We&apos;ll build the export in the background. A progress bar will appear at the bottom of
        the page, and a download button will be there when it&apos;s ready.
      </Typography>

      <div className={classes.formatSection}>
        <Typography className={classes.formatTitle} variant="subtitle2">
          Export format
        </Typography>
        <RadioGroup
          value={format}
          onChange={e => setFormat(e.target.value as Apollo.BulkExportFormat)}
        >
          {FORMAT_OPTIONS.map(option => (
            <FormControlLabel
              key={option.value}
              className={classes.formatLabel}
              control={<Radio />}
              label={
                <div className={classes.formatOptionPrimary}>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography className={classes.formatOptionDescription} variant="caption">
                    {option.description}
                  </Typography>
                </div>
              }
              value={option.value}
            />
          ))}
        </RadioGroup>
      </div>

      <Typography className={classes.expiry} color="textSecondary" variant="caption">
        Download links expire after 7 days.
      </Typography>

      <DialogActions>
        <AlineButton color="primary" disabled={loading} variant="outlined" onClick={onClose}>
          Cancel
        </AlineButton>
        <AlineButton
          className={classes.primaryButton}
          color="primary"
          loading={loading}
          variant="contained"
          onClick={handleConfirm}
        >
          Start export
        </AlineButton>
      </DialogActions>
    </AlineDialog>
  );
}
